import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCalendarDay,
  faClock,
  faCopy,
  faGlobe,
  faPhone,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  where,
  query
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase.js';
import { BRAND_NAME, ORGANIZATION_NAME } from '../../branding';
import { logAdminActivity } from '../../utils/activityLogger';
import {
  buildConfirmationEmailHtml,
  buildConfirmationSms,
  buildReminderEmailHtml,
  buildReminderSms,
  EMAIL_API_URL,
  formatCreatedAt,
  formatDateTime,
  formatDeliverySummary,
  formatReminderLabel,
  getCallStatus,
  getLabMeetingLink,
  getCalendarLink,
  LAB_REMINDER_LOGS_COLLECTION,
  SMS_API_URL
} from './labOnboardingShared';
import '../dash.css';

function ViewLabClientComp() {
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const callId = params.id || location.state?.myData || '';
  const hasLoggedViewRef = useRef(false);
  const hasMarkedViewedRef = useRef(false);

  const [currentUser, setCurrentUser] = useState(() => auth.currentUser);
  const [selectedCall, setSelectedCall] = useState(null);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    hasLoggedViewRef.current = false;
    hasMarkedViewedRef.current = false;
  }, [callId]);

  useEffect(() => {
    if (!callId) {
      setLoading(false);
      setSelectedCall(null);
      return undefined;
    }

    const unsubscribe = onSnapshot(doc(db, 'scheduledCalls', callId), (snapshot) => {
      if (!snapshot.exists()) {
        setSelectedCall(null);
        setLoading(false);
        return;
      }

      setSelectedCall({ id: snapshot.id, ...snapshot.data() });
      setLoading(false);
    }, (error) => {
      console.error('Failed to load lab client:', error);
      setSelectedCall(null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [callId]);

  useEffect(() => {
    if (!callId) {
      setReminderLogs([]);
      return undefined;
    }

    const reminderQuery = query(
      collection(db, LAB_REMINDER_LOGS_COLLECTION),
      where('scheduledCallId', '==', callId)
    );

    const unsubscribe = onSnapshot(reminderQuery, (snapshot) => {
      const nextLogs = [];

      snapshot.forEach((docItem) => {
        nextLogs.push({ id: docItem.id, ...docItem.data() });
      });

      nextLogs.sort((first, second) => {
        const firstTime = first.sentAt?.seconds || 0;
        const secondTime = second.sentAt?.seconds || 0;
        return secondTime - firstTime;
      });

      setReminderLogs(nextLogs);
    }, (error) => {
      console.error('Failed to load lab reminder logs:', error);
      setReminderLogs([]);
    });

    return () => unsubscribe();
  }, [callId]);

  useEffect(() => {
    if (!selectedCall || hasLoggedViewRef.current) return;

    hasLoggedViewRef.current = true;
    logAdminActivity({
      action: 'Opened Lab Client Page',
      details: `Viewed lab client page for ${selectedCall.name || 'client'}`,
      meta: {
        page: 'View Lab Client',
        scheduledCallId: selectedCall.id,
        clientName: selectedCall.name || '',
        clientEmail: selectedCall.email || ''
      }
    });
  }, [selectedCall]);

  useEffect(() => {
    if (!selectedCall || !currentUser || selectedCall.viewed || hasMarkedViewedRef.current) return;

    hasMarkedViewedRef.current = true;

    updateDoc(doc(db, 'scheduledCalls', selectedCall.id), {
      viewed: true,
      viewedAt: serverTimestamp(),
      viewedBy: currentUser.uid,
      viewedByEmail: currentUser.email || ''
    }).catch((error) => {
      hasMarkedViewedRef.current = false;
      console.error('Failed to mark lab client as viewed:', error);
    });
  }, [selectedCall, currentUser]);

  const suggestedReminder = useMemo(() => {
    if (!selectedCall) return null;

    const hasConfirmation = reminderLogs.some((log) => log.reminderType === 'confirmation' && log.anyChannelSucceeded);
    const hasPreCall = reminderLogs.some((log) => log.reminderType === 'pre_call' && log.anyChannelSucceeded);
    const status = getCallStatus(selectedCall);

    if (!hasConfirmation) {
      return {
        title: 'Send booking confirmation',
        description: 'New bookings should get a confirmation right away so the client knows the session is secured and has the meeting link.',
        actionLabel: 'Send confirmation now',
        reminderType: 'confirmation'
      };
    }

    if (status.needsReminder && !hasPreCall) {
      return {
        title: 'Send a pre-call reminder',
        description: 'This meeting is getting close. A reminder now helps the client show up prepared and keeps the conversation warm.',
        actionLabel: 'Send pre-call reminder',
        reminderType: 'pre_call'
      };
    }

    return {
      title: 'Client is in the loop',
      description: 'The client has already been contacted. You can still send another reminder if timing changes or you need to re-engage them.',
      actionLabel: 'Send another reminder',
      reminderType: 'pre_call'
    };
  }, [reminderLogs, selectedCall]);

  const handleCopyLink = async () => {
    const meetingLink = getLabMeetingLink(selectedCall);

    if (!meetingLink) {
      swal('Missing Link', 'This client does not have a room link yet.', 'warning');
      return;
    }

    try {
      await navigator.clipboard.writeText(meetingLink);
      logAdminActivity({
        action: 'Copy Lab Meeting Link',
        details: `Copied onboarding meeting link for ${selectedCall.name || 'client'}`,
        meta: { page: 'View Lab Client', clientEmail: selectedCall.email || '', roomId: selectedCall.roomId || '' }
      });
      swal('Copied', 'Meeting link copied to clipboard.', 'success');
    } catch (error) {
      console.error('Failed to copy room link:', error);
      swal('Error', 'Could not copy the meeting link.', 'error');
    }
  };

  const handleJoinCall = () => {
    const meetingLink = getLabMeetingLink(selectedCall);

    if (!meetingLink) {
      swal('Missing Link', 'This client does not have a room link yet.', 'warning');
      return;
    }

    logAdminActivity({
      action: 'Open Lab Meeting',
      details: `Opened onboarding call for ${selectedCall.name || 'client'}`,
      meta: { page: 'View Lab Client', clientEmail: selectedCall.email || '', roomId: selectedCall.roomId || '' }
    });

    window.open(meetingLink, '_blank', 'noopener,noreferrer');
  };

  const sendReminder = async (channel, reminderType = 'pre_call') => {
    const currentUser = auth.currentUser;
    if (!currentUser || !selectedCall) {
      swal('Session expired', 'Please log in again before sending reminders.', 'error');
      return;
    }

    const wantsEmail = channel === 'email' || channel === 'both';
    const wantsSms = channel === 'sms' || channel === 'both';

    if (wantsEmail && !selectedCall.email && !wantsSms) {
      swal('Missing Email', 'This client did not provide an email address.', 'warning');
      return;
    }

    if (wantsSms && !selectedCall.phone && !wantsEmail) {
      swal('Missing Phone', 'This client did not provide a phone number.', 'warning');
      return;
    }

    if ((wantsEmail && !selectedCall.email) && (wantsSms && !selectedCall.phone)) {
      swal('Missing Contacts', 'This client has neither an email address nor a phone number for reminders.', 'warning');
      return;
    }

    const confirmed = await swal({
      title: `Send ${formatReminderLabel(reminderType).toLowerCase()}?`,
      text: `This will send ${channel === 'both' ? 'email and SMS' : channel} updates to ${selectedCall.name || 'this client'}.`,
      icon: 'warning',
      buttons: true
    });

    if (!confirmed) return;

    const actionKey = `${selectedCall.id}:${channel}:${reminderType}`;
    setActionLoading((prev) => ({ ...prev, [actionKey]: true }));

    try {
      const token = await currentUser.getIdToken();
      const isConfirmation = reminderType === 'confirmation';
      const delivery = {
        email: { attempted: false, success: false, skipped: !wantsEmail || !selectedCall.email, error: '' },
        sms: { attempted: false, success: false, skipped: !wantsSms || !selectedCall.phone, error: '' }
      };

      if (wantsEmail && selectedCall.email) {
        delivery.email.attempted = true;
        const response = await fetch(EMAIL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email: selectedCall.email,
            subject: isConfirmation
              ? `Confirmed: ${selectedCall.help || `Your ${ORGANIZATION_NAME} session`}`
              : `Reminder: ${selectedCall.help || `Your ${ORGANIZATION_NAME} session`}`,
            message: isConfirmation ? buildConfirmationEmailHtml(selectedCall) : buildReminderEmailHtml(selectedCall)
          })
        });

        delivery.email.success = response.ok;
        if (!response.ok) {
          delivery.email.error = `Email request failed with status ${response.status}`;
        }
      }

      if (wantsSms && selectedCall.phone) {
        delivery.sms.attempted = true;
        const response = await fetch(SMS_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            phoneNumber: selectedCall.phone,
            message: isConfirmation ? buildConfirmationSms(selectedCall) : buildReminderSms(selectedCall)
          })
        });

        delivery.sms.success = response.ok;
        if (!response.ok) {
          delivery.sms.error = `SMS request failed with status ${response.status}`;
        }
      }

      const anyChannelSucceeded = Boolean(delivery.email.success || delivery.sms.success);

      await addDoc(collection(db, LAB_REMINDER_LOGS_COLLECTION), {
        scheduledCallId: selectedCall.id,
        clientName: selectedCall.name || '',
        clientEmail: selectedCall.email || '',
        clientPhone: selectedCall.phone || '',
        reminderType,
        channel,
        anyChannelSucceeded,
        delivery,
        roomId: selectedCall.roomId || '',
        roomUrl: getLabMeetingLink(selectedCall) || '',
        sentBy: currentUser.uid,
        sentByEmail: currentUser.email || '',
        sentAt: serverTimestamp()
      });

      logAdminActivity({
        action: 'Send Lab Reminder',
        details: `Sent ${formatReminderLabel(reminderType).toLowerCase()} to ${selectedCall.name || 'client'}`,
        meta: {
          page: 'View Lab Client',
          channel,
          reminderType,
          clientEmail: selectedCall.email || '',
          clientPhone: selectedCall.phone || '',
          roomId: selectedCall.roomId || ''
        }
      });

      if (!anyChannelSucceeded) {
        throw new Error('No reminder channel succeeded.');
      }

      swal(
        'Reminder sent',
        `${formatReminderLabel(reminderType)} sent successfully${channel === 'both' ? ' on the available channels' : ''}.`,
        'success'
      );
    } catch (error) {
      console.error(`Failed to send ${channel} reminder:`, error);
      swal('Error', `Could not send the ${formatReminderLabel(reminderType).toLowerCase()}.`, 'error');
    } finally {
      setActionLoading((prev) => ({ ...prev, [actionKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="clientsComp applicationsPage labOnboardingPage">
        <div className="incomeStateCard">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading client...</span>
          </Spinner>
          <p>Loading client details...</p>
        </div>
      </div>
    );
  }

  if (!selectedCall) {
    return (
      <div className="clientsComp applicationsPage labOnboardingPage">
        <div className="incomeStateCard">
          <p>This client record could not be found.</p>
          <Button variant="dark" onClick={() => navigate('/lab-onboarding')}>
            Back to recent clients
          </Button>
        </div>
      </div>
    );
  }

  const status = getCallStatus(selectedCall);
  const calendarLink = getCalendarLink(selectedCall);
  const meetingLink = getLabMeetingLink(selectedCall);

  return (
    <div className="clientsComp applicationsPage labOnboardingPage">
      <div className="appsHeader">
        <div>
          <button type="button" className="appsViewBtn labBackButton" onClick={() => navigate('/lab-onboarding')}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to recent clients
          </button>
          <p className="appsKicker">{BRAND_NAME}</p>
          <h4>{selectedCall.name || 'Client'}</h4>
          <p className="appsSubtext">{selectedCall.help || 'Product discovery call'}</p>
        </div>
      </div>

      <div className="labSelectedClientStack">
        <div className="labDetailHero">
          <div className="labDetailHeroTop">
            <div>
              <p className="appsKicker">Selected client</p>
              <h5>{selectedCall.name || 'Client'}</h5>
              <p>{selectedCall.help || 'Product discovery call'}</p>
            </div>
            <span className={`labStatusBadge labStatusBadge--${status.tone}`}>
              {status.label}
            </span>
          </div>

          <div className="labDetailMetaGrid">
            <div>
              <span><FontAwesomeIcon icon={faPhone} /> Phone</span>
              <strong>{selectedCall.phone || 'No phone provided'}</strong>
            </div>
            <div>
              <span><FontAwesomeIcon icon={faGlobe} /> Country</span>
              <strong>{selectedCall.country || 'Not captured'}</strong>
            </div>
            <div>
              <span><FontAwesomeIcon icon={faCalendarDay} /> Scheduled</span>
              <strong>{formatDateTime(selectedCall.date, selectedCall.time)}</strong>
            </div>
            <div>
              <span><FontAwesomeIcon icon={faVideo} /> Meeting room</span>
              <strong>{selectedCall.roomId || 'Room pending'}</strong>
            </div>
          </div>
        </div>

        <div className="labSelectedClientGrid">
          <div className="labQuickActionsCard">
            <div className="smsActionIntro">
              <div className="smsActionIcon">
                <FontAwesomeIcon icon={faVideo} />
              </div>
              <div>
                <strong>Meeting actions</strong>
                <span>Open the room fast, copy the join link, or drop the event into Google Calendar.</span>
              </div>
            </div>
            <div className="smsActionButtons">
              <button type="button" className="appsViewBtn" onClick={handleJoinCall}>
                Join video call
              </button>
              <button type="button" className="appsViewBtn" onClick={handleCopyLink}>
                <FontAwesomeIcon icon={faCopy} />
                Copy link
              </button>
              {calendarLink && (
                <a href={calendarLink} target="_blank" rel="noreferrer" className="appsViewBtn labLinkButton">
                  <FontAwesomeIcon icon={faCalendarDay} />
                  Add to Calendar
                </a>
              )}
            </div>
          </div>

          <div className="labQuickActionsCard">
            {suggestedReminder && (
              <div className="labRecommendationCard">
                <div>
                  <p className="labRecommendationLabel">Recommended next step</p>
                  <strong>{suggestedReminder.title}</strong>
                  <span>{suggestedReminder.description}</span>
                </div>
                <button
                  type="button"
                  className="appsViewBtn"
                  onClick={() => sendReminder('both', suggestedReminder.reminderType)}
                  disabled={Boolean(actionLoading[`${selectedCall.id}:both:${suggestedReminder.reminderType}`])}
                >
                  {actionLoading[`${selectedCall.id}:both:${suggestedReminder.reminderType}`]
                    ? 'Sending...'
                    : suggestedReminder.actionLabel}
                </button>
              </div>
            )}

            <div className="smsActionIntro">
              <div className="smsActionIcon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div>
                <strong>Reminder actions</strong>
                <span>Send the meeting details back to the client and keep them warm before the session starts.</span>
              </div>
            </div>
            <div className="smsActionButtons">
              <button
                type="button"
                className="appsViewBtn"
                onClick={() => sendReminder('both', 'confirmation')}
                disabled={Boolean(actionLoading[`${selectedCall.id}:both:confirmation`])}
              >
                {actionLoading[`${selectedCall.id}:both:confirmation`] ? 'Sending confirmation...' : 'Send confirmation'}
              </button>
              <button
                type="button"
                className="appsViewBtn"
                onClick={() => sendReminder('both', 'pre_call')}
                disabled={Boolean(actionLoading[`${selectedCall.id}:both:pre_call`])}
              >
                {actionLoading[`${selectedCall.id}:both:pre_call`] ? 'Sending reminder...' : 'Send email + SMS reminder'}
              </button>
              <button
                type="button"
                className="appsViewBtn"
                onClick={() => sendReminder('email', 'pre_call')}
                disabled={Boolean(actionLoading[`${selectedCall.id}:email:pre_call`])}
              >
                {actionLoading[`${selectedCall.id}:email:pre_call`] ? 'Sending email...' : 'Email only'}
              </button>
              <button
                type="button"
                className="appsViewBtn"
                onClick={() => sendReminder('sms', 'pre_call')}
                disabled={Boolean(actionLoading[`${selectedCall.id}:sms:pre_call`])}
              >
                {actionLoading[`${selectedCall.id}:sms:pre_call`] ? 'Sending SMS...' : 'SMS only'}
              </button>
            </div>
          </div>

          <div className="appsTableCard labInfoCard">
            <div className="appsTableHeader">
              <h6>Client details</h6>
              <span>Captured from the booking form</span>
            </div>
            <div className="labInfoBody">
              <div className="labInfoRow">
                <span>Email</span>
                <strong>{selectedCall.email || 'Not available'}</strong>
              </div>
              <div className="labInfoRow">
                <span>Room ID</span>
                <strong>{selectedCall.roomId || 'Not available'}</strong>
              </div>
              <div className="labInfoRow">
                <span>Meeting URL</span>
                <strong className="labBreakText">{meetingLink || 'Not available'}</strong>
              </div>
              <div className="labInfoRow">
                <span>Submitted</span>
                <strong>{formatCreatedAt(selectedCall.timestamp)}</strong>
              </div>
              <div className="labInfoRow">
                <span>Created by</span>
                <strong>{selectedCall.createdBy || 'System'}</strong>
              </div>
            </div>
          </div>

          <div className="appsTableCard labInfoCard labReminderHistoryCard">
            <div className="appsTableHeader">
              <h6>Reminder history</h6>
              <span>{reminderLogs.length} logged actions</span>
            </div>
            <div className="labReminderHistory">
              {reminderLogs.length === 0 ? (
                <p className="labReminderEmpty">No reminders have been sent to this client yet.</p>
              ) : (
                reminderLogs.map((log) => (
                  <div key={log.id} className="labReminderHistoryItem">
                    <div className="labReminderHistoryHead">
                      <div>
                        <strong>{formatReminderLabel(log.reminderType)}</strong>
                        <span>{formatCreatedAt(log.sentAt)}</span>
                      </div>
                      <span className={`labReminderDeliveryTag ${log.anyChannelSucceeded ? 'success' : 'failed'}`}>
                        {formatDeliverySummary(log)}
                      </span>
                    </div>
                    <div className="labReminderHistoryChannels">
                      <span>Email: {log.delivery?.email?.success ? 'Sent' : (log.delivery?.email?.skipped ? 'Skipped' : 'Failed')}</span>
                      <span>SMS: {log.delivery?.sms?.success ? 'Sent' : (log.delivery?.sms?.skipped ? 'Skipped' : 'Failed')}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewLabClientComp;
